// src/pages/Subscription.jsx
import Header from "components/Header";
import React, { useState, useEffect } from "react";
import subBG from "assets/BG illustration.png";
import { MdFoundation, MdOutlineHomeWork } from "react-icons/md";
import { Check, Crown, Sparkles, AlertTriangle } from "lucide-react";
import axios from "axios";
import newRelease from "assets/new_releases.svg";
import { useAlert } from "components/UI_components/Alert";
import { BASE_URL } from "utils/constant";
import SubscriptionCheckout from "./SubscriptionCheckout";

function Subscription({ prepareForLogout }) {
  const { addAlert } = useAlert();

  // UI: period selector (kept from your page)
  const periods = ["Monthly", "Annually", "4 months", "6 months"];
  const [selectedDuration, setSelectedDuration] = useState("Monthly");

  // Data from API
  const [subscriptionPlans, setSubscriptionPlans] = useState([]);
  const [userSubscription, setUserSubscription] = useState(null);

  // UX state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);

  // Map period names to API naming pattern
  const periodMapping = {
    Monthly: "Monthly Plan",
    Annually: "Yearly Plan",
    "4 months": "4 months",
    "6 months": "6 months",
  };

  // Fetch: all plans
  useEffect(() => {
    const fetchSubscriptionPlans = async () => {
      try {
        setLoading(true);
        const accessToken = localStorage.getItem("access");
        const response = await axios.get(
          `${BASE_URL}/subscription/get-all-subscriptionplans/`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (response.data?.status) {
          setSubscriptionPlans(response.data.data || []);
        } else {
          setError("Failed to load subscription plans");
        }
      } catch (err) {
        console.error("Subscription fetch error:", err);
        setError("Error connecting to subscription service");
      } finally {
        setLoading(false);
      }
    };
    fetchSubscriptionPlans();
  }, []);

  // Fetch: current user's org subscription
  useEffect(() => {
    const fetchUserSubscription = async () => {
      try {
        const organization = JSON.parse(localStorage.getItem("organization"));
        if (!organization?.id) return;
        const accessToken = localStorage.getItem("access");
        const response = await axios.post(
          `${BASE_URL}/subscription/get-user-subscription/`,
          { organization_id: organization.id },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
              "Content-Type": "application/json",
            },
          }
        );
        if (response.data?.status) {
          setUserSubscription(response.data.data);
        }
      } catch (err) {
        console.error("User subscription fetch error:", err);
      }
    };
    fetchUserSubscription();
  }, []);

  // Helpers
  const getApiPlansForPeriod = (
    periodName,
    featureType /* 2=Basic, 3=Premium */
  ) => {
    const pattern = periodMapping[periodName];
    return subscriptionPlans.filter(
      (plan) =>
        plan.subscription_name?.toLowerCase().includes(pattern.toLowerCase()) &&
        plan.feature === featureType
    );
  };

  // Free plan (static + ID pulled from API list)
  const getFreePlan = () => ({
    name: "Free trial",
    price: null,
    planId: subscriptionPlans?.find((p) =>
      p?.subscription_name?.toLowerCase().includes("free")
    )?.id,
    icon: Sparkles,
    buttonText: "Start 7 days free trial",
    buttonVariant: "outline",
    features: [
      "With ads",
      "Access organization dashboard",
      "Add upto 10 workers & 5 contractors",
      "Access only Inventory & Delivery tabs",
      "Materials: Add upto 10 materials",
      "Monthly reports: Download upto 2 reports",
    ],
  });

  const getPlansForSelectedPeriod = () => {
    const basicApi = getApiPlansForPeriod(selectedDuration, 2)[0] || null;
    const premiumApi = getApiPlansForPeriod(selectedDuration, 3)[0] || null;

    const plans = [getFreePlan()];

    if (basicApi) {
      plans.push({
        name: "Basic",
        price: basicApi.total_charge?.toString() ?? "",
        savePercent: "17%",
        planId: basicApi.id,
        razorpayPlanId: basicApi.razorpay_subscription_plan_id,
        icon: Crown,
        buttonText: "Select plan",
        buttonVariant: "default",
        features: [
          "Ads-free",
          "Access organization dashboard",
          "Access all 3 tabs (Inventory/Delivery/Vendors)",
          "Add upto 50 workers & 25 contractors",
          "Excel import/export feature",
          "Members: Add upto 5 members",
          "Materials: Add upto 30 materials",
          "Vendors: Add upto 5 vendors",
          `Monthly reports: Download upto ${
            selectedDuration === "Annually"
              ? "100"
              : selectedDuration === "4 months"
              ? "45"
              : selectedDuration === "6 months"
              ? "10"
              : "70"
          } reports`,
          `Customer support ticket: Raise upto ${
            selectedDuration === "Annually"
              ? "25"
              : selectedDuration === "4 months"
              ? "10"
              : selectedDuration === "6 months"
              ? "2"
              : "10"
          } tickets with an average response time of 7 days & resolution time of 20 days`,
        ],
      });
    }

    if (premiumApi) {
      plans.push({
        name: "Premium",
        price: premiumApi.total_charge?.toString() ?? "",
        planId: premiumApi.id,
        razorpayPlanId: premiumApi.razorpay_subscription_plan_id,
        icon: Crown,
        buttonText: "Select plan",
        buttonVariant: "default",
        isBestValue: true,
        features: [
          "Ads-free",
          "Access organization dashboard",
          "Access all 3 tabs (Inventory/Delivery/Vendors)",
          "Add upto unlimited workers & contractors",
          "Excel import/export feature",
          "Members: Add upto 15 members",
          "Materials: Add unlimited materials",
          "Vendors: Add unlimited vendors",
          "Monthly reports: Download unlimited reports",
          `Customer support ticket: Raise upto ${
            selectedDuration === "Annually"
              ? "30"
              : selectedDuration === "4 months"
              ? "15"
              : selectedDuration === "6 months"
              ? "4"
              : "20"
          } tickets with an average response time of 2 days & resolution time of 10 days`,
        ],
      });
    }

    return plans;
  };

  const startFreeTrial = async () => {
    try {
      setLoading(true);
      const accessToken = localStorage.getItem("access");
      const freePlanId = getFreePlan().planId;
      if (!freePlanId) {
        setLoading(false);
        addAlert(
          "Free Trial plan not available. Please try again later.",
          "error"
        );
        return;
      }
      const response = await axios.post(
        `${BASE_URL}/subscription/create-subscription/`,
        { subscription_plan_id: freePlanId },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );

      if (response.data?.status) {
        addAlert("Free trial started successfully", "success");
      } else {
        addAlert(response.data?.error || "Error starting Free Trial", "error");
      }
    } catch (err) {
      console.error("Error starting free trial:", err);
      addAlert("Error starting Free Trial", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectPlan = (plan) => {
    if (!plan.price) {
      // Free trial
      startFreeTrial();
      return;
    }
    setSelectedPlan(plan);
    setShowCheckout(true);
  };

  const handleBackToPlans = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  const renderActiveSubscriptionWarning = () => {
    if (
      userSubscription?.razorpay_subscription &&
      userSubscription?.active_flag === true &&
      new Date(userSubscription?.valid_till) > new Date()
    ) {
      return (
        <div className="mx-5 mt-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle
            className="text-yellow-600 flex-shrink-0 mt-1"
            size={24}
          />
          <div>
            <p className="text-yellow-800 font-semibold mb-1">
              Active Subscription Detected
            </p>
            <p className="text-yellow-700 text-sm mb-2">
              You are currently on{" "}
              <span className="font-bold">
                {userSubscription.subscription_name}
              </span>{" "}
              until {new Date(userSubscription.valid_till).toLocaleDateString()}
              .
            </p>
            <p className="text-yellow-600 text-xs">
              <strong>Warning:</strong> Buying a new plan will immediately
              terminate the current one and cannot be undone.
            </p>
          </div>
        </div>
      );
    }
    return null;
  };

  // ---------- RENDER ----------
  return (
    <div
      className="flex flex-col h-full bg-white"
      style={{ scrollbarWidth: "none" }}
    >
      <Header
        isLogo={true}
        isHome={true}
        isBell={false}
        prepareForLogout={prepareForLogout}
      />

      {/* Hero */}
      <div
        className="flex flex-col w-full items-center justify-center h-[40vh]"
        style={{
          background:
            "linear-gradient(to bottom, #E0ADFF 0%, #E0ADFF34 60%, #ffffff 100%)",
        }}
      >
        <img
          src={subBG}
          alt="Background"
          className="relative object-cover h-[90%] w-[75%]"
          style={{ alignSelf: "center" }}
        />
        <div className="flex flex-col -mt-32 z-10 w-[65%] items-center gap-2">
          <span className="text-3xl font-bold text-center text-[#3B0069]">
            Subscription plans
          </span>
          <span className="text-base font-normal text-center text-[#737373]">
            Pick the plan that is right for you and subscribe to SiteMaster
          </span>
        </div>
      </div>

      {/* Period tabs (kept your look & feel) */}
      <div className="flex flex-row mt-4 items-end justify-center gap-6 w-full border-b border-[#3B0069]">
        {periods.map((d) => {
          const active = selectedDuration === d;
          return (
            <button
              key={d}
              onClick={() => setSelectedDuration(d)}
              className={[
                "px-12 py-1 font-medium border-x-[1px] border-t-[1px] rounded-t-lg cursor-pointer -mb-px transition-colors relative",
                active
                  ? "text-[#3B0069]  border-x-[#3B0069] border-t-[#3B0069] border-b-white border-b-[3px]"
                  : "text-[#737373] border-x-[#CCCCCC] border-t-[#CCCCCC] border-b-transparent",
              ].join(" ")}
            >
              {d}
              {d === "Annually" && (
                <span className="absolute -top-2 -right-2 bg-[#3B0069] text-white text-xs px-1.5 py-0.5 rounded-md font-medium">
                  Save 45%
                </span>
              )}
              {d === "4 months" && (
                <span className="absolute -top-2 -right-2 bg-[#3B0069] text-white text-xs px-1.5 py-0.5 rounded-md font-medium">
                  Save 33%
                </span>
              )}
              {d === "6 months" && (
                <span className="absolute -top-2 -right-2 bg-[#3B0069] text-white text-xs px-1.5 py-0.5 rounded-md font-medium">
                  Save 39%
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Active subscription warning */}
      {renderActiveSubscriptionWarning()}

      {/* Loading / Error */}
      {loading && (
        <div className="flex justify-center items-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#3B0069]"></div>
        </div>
      )}
      {error && !loading && (
        <div className="text-center py-16">
          <p className="text-red-500">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 bg-[#3B0069] text-white px-4 py-2 rounded-lg hover:opacity-90 transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {/* Checkout view */}
      {!loading && !error && showCheckout && selectedPlan && (
        <div className="px-4 md:px-8 pb-10">
          <SubscriptionCheckout
            selectedPlan={selectedPlan}
            onClose={() => {
              setShowCheckout(false);
            }}
            onBack={handleBackToPlans}
          />
        </div>
      )}

      {/* --- PLANS (original UI preserved) --- */}
      {!loading && !error && !showCheckout && (
        <div className="flex flex-row mt-2 items-start justify-center w-full gap-5 py-2">
          {(() => {
            const plans = getPlansForSelectedPeriod().filter(
              (p) => p.name !== "Free trial"
            );
            const basic = plans.find((p) => p.name === "Basic");
            const premium = plans.find((p) => p.name === "Premium");

            const PlanCard = ({ plan, title }) => {
              const unavailable = !plan;
              return (
                <div className="flex flex-col h-[85vh] bg-[#F7EBFF] w-1/4 px-4 py-3 rounded-lg border-[1px] border-[#3B0069]">
                  <div className="flex flex-col items-start gap-6">
                    <div className="flex flex-row items-center justify-start gap-2">
                      {title === "Basic" ? (
                        <MdFoundation color="#3B0069" size={26} />
                      ) : (
                        <MdOutlineHomeWork color="#3B0069" size={26} />
                      )}
                      <span className="text-xl font-semibold text-[#3B0069]">
                        {title}
                      </span>
                    </div>

                    <div className="flex flex-row items-end justify-start gap-2">
                      <span className="text-3xl font-semibold text-[#3B0069]">
                        {unavailable ? "—" : `₹ ${plan.price}/-`}
                      </span>
                      <span className="text-base font-semibold text-black">
                        incl GST
                      </span>
                    </div>

                    <button
                      disabled={unavailable}
                      onClick={() => !unavailable && handleSelectPlan(plan)}
                      style={{ alignSelf: "center" }}
                      className={[
                        "flex items-center w-[95%] text-center justify-center flex-row px-6 py-2 rounded-lg text-white text-base font-normal",
                        unavailable
                          ? "bg-[#C7B2D6] cursor-not-allowed"
                          : "bg-[#3B0069] hover:opacity-90",
                      ].join(" ")}
                    >
                      {unavailable ? "Plan unavailable" : "Select Plan"}
                    </button>
                  </div>

                  {/* Features */}
                  <div className="flex flex-col mt-4 overflow-auto">
                    <span className="text-sm font-normal text-[#737373]">
                      Included in this plan
                    </span>
                    <ul className="list-none mt-3 flex flex-col gap-2">
                      {(plan?.features?.length
                        ? plan.features
                        : [
                            // graceful fallback if API has no features
                            "Access organization dashboard",
                          ]
                      ).map((feat) => (
                        <li
                          key={feat}
                          className="text-sm text-[#232323] flex flex-row items-start justify-start gap-2"
                        >
                          <img
                            src={newRelease}
                            alt="New Release"
                            className="w-4 h-4 mt-[2px]"
                          />
                          <span>{feat}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            };

            return (
              <>
                <PlanCard plan={basic} title="Basic" />
                <PlanCard plan={premium} title="Premium" />
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
}

export default Subscription;
